/**
 * Manage, Onboarding templates: one template (plan docs/plans/lms-plan.md
 * 12.1, 5.7, 18.12, 18.24; WP-47; R14).
 *
 * GET /admin/onboarding/templates/:id answers { template, items,
 * retired_items }. Two parts:
 *
 *   Details: the name, the description, whether it is the default and
 *   whether it is in use (PATCH with the changed fields; a retired template
 *   cannot be the default; a duplicate name is 409 with existing_template).
 *
 *   Items: the ordered checklist. Each item is edited in place (ItemForm),
 *   moved with Move up and Move down (the list order is the ordinal), removed
 *   (an existing item is retired by the save, never deleted: a run's items
 *   point at it) and restored. "Save items" sends the whole ordered list with
 *   PUT .../items (ids kept) and shows the server's answer { items,
 *   retired_items }. A template edit never rewrites a run already started:
 *   each run copied its items (5.7).
 *
 * 18.12: the seeded default template carries no course links; the owner
 * links the course items here after the catalog install.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowDown, ArrowLeft, ArrowUp, Loader2, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useToast } from '../../../../ui/Toast';
import { fieldCls, iconBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { useLms } from '../../../LmsProvider';
import {
  existingTemplateOf,
  getOnboardingTemplate,
  ONBOARDING_LIMITS,
  replaceOnboardingTemplateItems,
  updateOnboardingTemplate,
} from '../../../api/onboarding';
import type { OnboardingTemplateDetail, OnboardingTemplateItem, OnboardingTemplatePatch } from '../../../api/onboarding';
import { checkboxCls, ErrorNote, labelCls, metaCls, Pill, WarningNote } from '../../../components/pickers/shared';
import { describeOnboardingError, EVIDENCE_LABELS, KindPill, RESPONSIBLE_LABELS } from '../../onboarding/shared';
import ItemForm from './ItemForm';
import { draftOf, hasProblems, itemProblems, listFingerprint, moveItem, newDraft, parseDueDays, toInput } from './model';
import type { DraftItem } from './model';
import { TEMPLATES_BASE, templateHref } from './paths';

const cardCls = 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800';
const inputCls = `${fieldCls} min-h-[44px] w-full sm:min-h-0`;

/** The route element: one editor per template id, so a new id starts clean. */
export function TemplateEditorRoute() {
  const { templateId } = useParams();
  if (!templateId) return null;
  return <TemplateEditor key={templateId} templateId={templateId} />;
}

export function TemplateEditor({ templateId }: { templateId: string }) {
  const { siteId } = useLms();
  const [detail, setDetail] = useState<OnboardingTemplateDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getOnboardingTemplate(templateId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) setError(describeOnboardingError(err, 'This template could not be loaded.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [templateId, siteId, tick]);

  const back = (
    <Link
      to={TEMPLATES_BASE}
      className="inline-flex min-h-[44px] items-center gap-1 rounded text-sm font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 sm:min-h-0"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      All onboarding templates
    </Link>
  );

  if (!detail) {
    return (
      <div className="space-y-4" data-testid="onboarding-template-editor">
        {back}
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
            Loading the template
          </p>
        ) : (
          <ErrorNote testId="onboarding-template-error">
            {error ?? 'This template could not be loaded.'}{' '}
            <button type="button" className="font-medium underline" onClick={() => setTick((t) => t + 1)}>
              Retry
            </button>
          </ErrorNote>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="onboarding-template-editor">
      {back}
      <DetailsCard detail={detail} onSaved={(template) => setDetail((d) => (d ? { ...d, template } : d))} />
      <ItemsEditor
        detail={detail}
        onSaved={(items, retired) => setDetail((d) => (d ? { ...d, items, retired_items: retired } : d))}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Details (12.1: name, description, default, in use)
// ---------------------------------------------------------------------------

function DetailsCard({
  detail,
  onSaved,
}: {
  detail: OnboardingTemplateDetail;
  onSaved: (template: OnboardingTemplateDetail['template']) => void;
}) {
  const { toast } = useToast();
  const t = detail.template;
  const [name, setName] = useState(t.name);
  const [description, setDescription] = useState(t.description ?? '');
  const [isDefault, setIsDefault] = useState(t.is_default);
  const [isActive, setIsActive] = useState(t.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<string | null>(null);

  const patch = useMemo(() => {
    const p: OnboardingTemplatePatch = {};
    if (name.trim() !== t.name) p.name = name.trim();
    if ((description.trim() || null) !== (t.description ?? null)) p.description = description.trim() || null;
    if (isDefault !== t.is_default) p.is_default = isDefault;
    if (isActive !== (t.is_active !== false)) p.is_active = isActive;
    return p;
  }, [name, description, isDefault, isActive, t]);

  const nameProblem = !name.trim()
    ? 'A template needs a name.'
    : name.trim().length > ONBOARDING_LIMITS.templateName
      ? `A name is at most ${ONBOARDING_LIMITS.templateName} characters.`
      : null;
  const dirty = Object.keys(patch).length > 0;
  const canSave = dirty && !nameProblem && !saving;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setExistingId(null);
    try {
      const saved = await updateOnboardingTemplate(t.id, patch);
      onSaved(saved);
      setName(saved.name);
      setDescription(saved.description ?? '');
      setIsDefault(saved.is_default);
      setIsActive(saved.is_active !== false);
      toast('Template details saved.', { type: 'success' });
    } catch (err) {
      const existing = existingTemplateOf(err);
      setExistingId(existing && existing.id !== t.id ? existing.id : null);
      setError(describeOnboardingError(err, 'The template details could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className={`${cardCls} space-y-3`} data-testid="onboarding-template-details" aria-labelledby="lms-template-details-title">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="lms-template-details-title" className="text-base font-semibold text-gray-900 dark:text-gray-100" data-testid="onboarding-template-name">
          {t.name}
        </h2>
        {t.is_default ? <Pill tone="primary">Default</Pill> : null}
        {t.is_active === false ? <Pill tone="gray">Retired</Pill> : <Pill tone="green">Active</Pill>}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label htmlFor="lms-template-name" className={labelCls}>
            Name
          </label>
          <input
            id="lms-template-name"
            className={inputCls}
            value={name}
            maxLength={ONBOARDING_LIMITS.templateName + 20}
            onChange={(e) => setName(e.target.value)}
            data-testid="onboarding-template-name-input"
          />
          {nameProblem ? <p className="mt-1 text-xs text-red-700 dark:text-red-400">{nameProblem}</p> : null}
        </div>
        <div>
          <label htmlFor="lms-template-description" className={labelCls}>
            Description <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
          </label>
          <input
            id="lms-template-description"
            className={inputCls}
            value={description}
            maxLength={ONBOARDING_LIMITS.templateDescription}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="onboarding-template-description-input"
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm text-gray-800 dark:text-gray-200 sm:min-h-0">
          <input
            type="checkbox"
            className={checkboxCls}
            checked={isDefault}
            disabled={!isActive}
            onChange={(e) => setIsDefault(e.target.checked)}
            data-testid="onboarding-template-default"
          />
          The default template
        </label>
        <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm text-gray-800 dark:text-gray-200 sm:min-h-0">
          <input
            type="checkbox"
            className={checkboxCls}
            checked={isActive}
            onChange={(e) => {
              setIsActive(e.target.checked);
              if (!e.target.checked) setIsDefault(false);
            }}
            data-testid="onboarding-template-active"
          />
          In use (a retired template cannot start onboarding)
        </label>
        <button type="submit" className={`${secondaryBtnCls} ml-auto`} disabled={!canSave} data-testid="onboarding-template-details-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          Save details
        </button>
      </div>
      {error ? (
        <ErrorNote testId="onboarding-template-details-error">
          <p>{error}</p>
          {existingId ? (
            <Link to={templateHref(existingId)} className={`${secondaryBtnCls} mt-2`}>
              Open the existing template
            </Link>
          ) : null}
        </ErrorNote>
      ) : null}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Items (12.1: in order, each with kind, responsibility, evidence, due days,
// a URL, an optional linked course and the verification flag)
// ---------------------------------------------------------------------------

function ItemsEditor({
  detail,
  onSaved,
}: {
  detail: OnboardingTemplateDetail;
  onSaved: (items: OnboardingTemplateItem[], retired: OnboardingTemplateItem[]) => void;
}) {
  const { toast } = useToast();
  const initial = useMemo(() => detail.items.slice().sort((a, b) => a.ordinal - b.ordinal).map(draftOf), [detail.items]);
  const [drafts, setDrafts] = useState<DraftItem[]>(initial);
  const [savedPrint, setSavedPrint] = useState(() => listFingerprint(initial));
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const problems = useMemo(() => new Map(drafts.map((d) => [d.key, itemProblems(d)])), [drafts]);
  const invalidCount = drafts.filter((d) => hasProblems(problems.get(d.key) ?? {})).length;
  const dirty = listFingerprint(drafts) !== savedPrint;
  const tooMany = drafts.length > ONBOARDING_LIMITS.itemsPerTemplate;
  const canSave = dirty && invalidCount === 0 && !tooMany && !saving;

  const listedIds = new Set(drafts.filter((d) => d.id).map((d) => d.id as string));
  const leavingItems = detail.items.filter((i) => !listedIds.has(i.id));
  const retiredItems = detail.retired_items.filter((i) => !listedIds.has(i.id)).sort((a, b) => a.ordinal - b.ordinal);

  const update = useCallback((next: DraftItem) => {
    setDrafts((list) => list.map((d) => (d.key === next.key ? next : d)));
  }, []);

  const move = (index: number, delta: number) => setDrafts((list) => moveItem(list, index, delta));

  const remove = (draft: DraftItem) => {
    setDrafts((list) => list.filter((d) => d.key !== draft.key));
    if (openKey === draft.key) setOpenKey(null);
  };

  const add = () => {
    const d = newDraft();
    setDrafts((list) => [...list, d]);
    setOpenKey(d.key);
  };

  const restore = (item: OnboardingTemplateItem) => {
    setDrafts((list) => [...list, draftOf(item)]);
  };

  const discard = () => {
    setDrafts(initial);
    setSavedPrint(listFingerprint(initial));
    setOpenKey(null);
    setError(null);
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const result = await replaceOnboardingTemplateItems(detail.template.id, drafts.map(toInput));
      const items = (result.items ?? []).slice().sort((a, b) => a.ordinal - b.ordinal);
      const fresh = items.map(draftOf);
      setDrafts(fresh);
      setSavedPrint(listFingerprint(fresh));
      setOpenKey(null);
      onSaved(items, result.retired_items ?? []);
      toast(`Saved ${items.length} ${items.length === 1 ? 'item' : 'items'}.`, { type: 'success' });
    } catch (err) {
      setError(describeOnboardingError(err, 'The items could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`${cardCls} space-y-3`} aria-labelledby="lms-template-items-title" data-testid="onboarding-template-items">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 id="lms-template-items-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {`Checklist items (${drafts.length})`}
          </h2>
          <p className={metaCls}>In the order the new team member sees them. A run already started keeps the items it copied.</p>
        </div>
        <button type="button" className={secondaryBtnCls} onClick={add} data-testid="template-item-add">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add item
        </button>
      </div>

      {drafts.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600 dark:border-gray-600 dark:text-gray-400">
          No items yet. Add the first thing a new team member does.
        </p>
      ) : (
        <ol className="space-y-2" aria-label="Checklist items in order">
          {drafts.map((d, index) => {
            const p = problems.get(d.key) ?? {};
            const open = openKey === d.key;
            const due = parseDueDays(d.due_days);
            return (
              <li
                key={d.key}
                className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                data-testid={`template-item-${d.key}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-6 flex-shrink-0 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400">{index + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100" data-testid={`template-item-name-${d.key}`}>
                      {d.title.trim() || 'Untitled item'}
                    </p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      <KindPill kind={d.kind} />
                      <span className={metaCls}>
                        {[
                          RESPONSIBLE_LABELS[d.responsible],
                          EVIDENCE_LABELS[d.evidence],
                          due === null ? null : `due day ${due}`,
                          d.requires_verification ? 'verified by an admin' : null,
                          d.id ? null : 'new',
                        ]
                          .filter(Boolean)
                          .join(' \u00b7 ')}
                      </span>
                      {hasProblems(p) ? <Pill tone="red">Needs attention</Pill> : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className={iconBtnCls}
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      aria-label={`Move ${d.title.trim() || 'this item'} up`}
                      data-testid={`template-item-up-${d.key}`}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className={iconBtnCls}
                      onClick={() => move(index, 1)}
                      disabled={index === drafts.length - 1}
                      aria-label={`Move ${d.title.trim() || 'this item'} down`}
                      data-testid={`template-item-down-${d.key}`}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className={iconBtnCls}
                      onClick={() => setOpenKey(open ? null : d.key)}
                      aria-expanded={open}
                      aria-label={`${open ? 'Close' : 'Edit'} ${d.title.trim() || 'this item'}`}
                      data-testid={`template-item-edit-${d.key}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className={iconBtnCls}
                      onClick={() => remove(d)}
                      aria-label={`Remove ${d.title.trim() || 'this item'}`}
                      data-testid={`template-item-remove-${d.key}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {open ? (
                  <div className="mt-3">
                    <ItemForm draft={d} problems={p} onChange={update} />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}

      {leavingItems.length > 0 ? (
        <WarningNote testId="template-items-leaving">
          <p className="font-medium">Retired when you save</p>
          <ul className="mt-1 space-y-1">
            {leavingItems.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center justify-between gap-2">
                <span>{i.title}</span>
                <button type="button" className={secondaryBtnCls} onClick={() => restore(i)} data-testid={`template-item-keep-${i.id}`}>
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Keep it
                </button>
              </li>
            ))}
          </ul>
        </WarningNote>
      ) : null}

      {retiredItems.length > 0 ? (
        <details className="rounded-lg border border-gray-200 p-3 dark:border-gray-700" data-testid="template-items-retired">
          <summary className="min-h-[44px] cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-200 sm:min-h-0">
            {`Retired items (${retiredItems.length})`}
          </summary>
          <ul className="mt-2 space-y-1">
            {retiredItems.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span>{i.title}</span>
                <button type="button" className={secondaryBtnCls} onClick={() => restore(i)} data-testid={`template-item-restore-${i.id}`}>
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {invalidCount > 0 ? (
        <p className="text-sm text-red-700 dark:text-red-400" data-testid="template-items-invalid">
          {`${invalidCount} ${invalidCount === 1 ? 'item needs' : 'items need'} attention before the list can be saved.`}
        </p>
      ) : null}
      {tooMany ? (
        <p className="text-sm text-red-700 dark:text-red-400">{`A template holds at most ${ONBOARDING_LIMITS.itemsPerTemplate} items.`}</p>
      ) : null}
      {error ? <ErrorNote testId="template-items-error">{error}</ErrorNote> : null}

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-3 dark:border-gray-700">
        {dirty ? <span className={metaCls}>Unsaved changes</span> : null}
        <button type="button" className={secondaryBtnCls} onClick={discard} disabled={!dirty || saving} data-testid="template-items-discard">
          Discard changes
        </button>
        <button type="button" className={primaryBtnCls} onClick={save} disabled={!canSave} data-testid="template-items-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          Save items
        </button>
      </div>
    </section>
  );
}

export default TemplateEditor;
